import {useState} from "react";
import {useSelector} from "react-redux";
import {selectUserId} from "../../store/features/Auth/authSlice.ts";
import {buildQueryParams} from "../../utilis/buildQueryParams.ts";
import {PhoneHQAvatar} from 'ch3-ui-lib';
import {Contact, useGetContactsInfiniteQuery} from "../../store/features/Contacts/contactsService.ts";
import {getFullName} from "../../utilis/getFullName.ts";

interface UseContactFiltersProps {
    selectedContacts: Contact[];
    setSelectedContacts: (contacts: Contact[]) => void;
    admin?: boolean;
}

interface ContactFiltersReturn {
    contactsPhrase: string;
    setContactsPhrase: (phrase: string) => void;
    contacts: any[];
    handleContactSelect: (value: Contact) => void;
    isLoading?: boolean;
    error?: any;
    onScrollEnd: () => void;
}

export const useContactFilters = ({
                                      selectedContacts,
                                      setSelectedContacts,
                                      admin = false
                                  }: UseContactFiltersProps): ContactFiltersReturn => {
    const [contactsPhrase, setContactsPhrase] = useState<string>('');
    const userId = useSelector(selectUserId);

    const contactsQueryParams = buildQueryParams({
        phrase: contactsPhrase,
        userId: admin ? undefined : userId, // If admin, don't filter by userId
    });

    const {
        data: contactsData,
        isLoading,
        error,
        fetchNextPage,
        hasNextPage
    } = useGetContactsInfiniteQuery(contactsQueryParams);

    const currentData = contactsData?.pages.flatMap(page => page.contacts) ?? [];

    const onScrollEnd = () => {
        if (!hasNextPage || isLoading) return;
        fetchNextPage();
    };

    const contacts = currentData
        .reduce<{ map: Map<number, boolean>; result: any[] }>((acc, contact) => {
            if (!acc.map.has(contact.contactId)) {
                acc.map.set(contact.contactId, true);
                acc.result.push({
                    key: contact.contactId,
                    label: getFullName(contact.firstName, contact.lastName),
                    hideDescription: true,
                    value: contact,
                    prefixElement: (
                        <div className='mx-2'>
                            <PhoneHQAvatar name={getFullName(contact.firstName, contact.lastName)} size="24px"/>
                        </div>
                    ),
                    type: 'checkbox'
                });
            }
            return acc;
        }, {map: new Map(), result: []}).result;

    const handleContactSelect = (value: Contact) => {
        const exists = selectedContacts.some(item => item.contactId === value.contactId);

        const newSelectedContacts = exists
            ? selectedContacts.filter(item => item.contactId !== value.contactId)
            : [...selectedContacts, value];

        setSelectedContacts(newSelectedContacts);
    };

    return {
        contactsPhrase,
        setContactsPhrase,
        contacts,
        handleContactSelect,
        isLoading,
        error,
        onScrollEnd
    };
};